import {HttpErrorResponse} from '@angular/common/http';
import {FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {AccountInterface} from '../../../../../model/account.interface';
import {
  IpopConfiguration, IpopConfigurationUpdate, SiteWideUnlockPageType, SiteWideUnlockPageTypes, SiteWideUnlockPreferences
} from '../../../../../model/ipop-configuration.model';
import {customPathChange, SiteWideUnlockPathForm, validCustomPathOptions} from './site-wide-unlock-paths';

export const siteWideTextLimit = 500;
export const siteWideLongTextLimit = 5000;
export const siteWideUrlLimit = 2048;
const colorPattern = /^#[0-9a-fA-F]{6}$/;
const httpUrlPattern = /^https?:\/\/[^/?#]+(?:[/?#]|$)/i;
const unsafeUrlCharacters = /[\x00-\x20\x7f\\]/;
const destinationError = 'Use an HTTP(S) offers URL on the account website hostname, or disable navigation.';

export const siteWideUnlockDefaults: SiteWideUnlockPreferences = {
  primaryColor: '#16a34a', secondaryColor: '#002c4c', ctaLabel: 'Unlock Instant Savings',
  formTitle: 'Unlock Instant Savings', formSubtitle: 'Get Access to Site-Wide Private Offers',
  bannerHeadline: 'Click Here to Unlock Instant Savings', bannerBody: 'Get Access to Site-Wide Private Offers',
  popupHeadline: 'Unlock Instant Savings', popupBody: 'Get Access to Site-Wide Private Offers',
  navigationLabel: 'View Unlocked Offers', destinationUrlOverride: null
};

type PresentationFormValue = Omit<SiteWideUnlockPreferences, 'destinationUrlOverride'> & {destinationUrlOverride: string};
type PresentationControls = {[Key in keyof PresentationFormValue]: FormControl<PresentationFormValue[Key]>};
type TextFieldName = Exclude<keyof SiteWideUnlockPreferences, 'primaryColor' | 'secondaryColor' | 'destinationUrlOverride'>;

export const siteWideTextFields: ReadonlyArray<{name: TextFieldName; label: string; limit: number}> = [
  {name: 'ctaLabel', label: 'CTA button label', limit: siteWideTextLimit},
  {name: 'formTitle', label: 'Unlock form title', limit: siteWideTextLimit},
  {name: 'formSubtitle', label: 'Unlock form subtitle', limit: siteWideLongTextLimit},
  {name: 'bannerHeadline', label: 'Banner headline', limit: siteWideTextLimit},
  {name: 'bannerBody', label: 'Banner body', limit: siteWideLongTextLimit},
  {name: 'popupHeadline', label: 'Popup headline', limit: siteWideTextLimit},
  {name: 'popupBody', label: 'Popup body', limit: siteWideLongTextLimit},
  {name: 'navigationLabel', label: 'Post-unlock navigation label', limit: siteWideTextLimit}
];

export const siteWidePageTypeOptions: {key: SiteWideUnlockPageType; label: string}[] = [
  {key: 'homepage', label: 'Homepage'},
  {key: 'vdp', label: 'Vehicle detail pages (VDP)'},
  {key: 'srp', label: 'Search results pages (SRP)'},
  {key: 'dt_landing_page', label: 'DT landing pages'}
];

export type SiteWideUnlockForm = FormGroup<{
  siteWideUnlockEnabled: FormControl<boolean>;
  bannerEnabled: FormControl<boolean>;
  unlockPopupEnabled: FormControl<boolean>;
  navigationPopupEnabled: FormControl<boolean>;
  unlockDesktop: FormControl<number | null>;
  navigationDesktop: FormControl<number | null>;
  pageTypes: FormGroup<{[Key in SiteWideUnlockPageType]: FormControl<boolean>}>;
  customPaths: FormArray<SiteWideUnlockPathForm>;
  saveError: FormControl<string>;
  requiresReload: FormControl<boolean>;
  presentation: FormGroup<PresentationControls>;
}>;

/** Counts Unicode characters like the API rather than treating surrogate pairs as two characters. */
function characterLimit(limit: number): ValidatorFn
{
  const validator: ValidatorFn = control => {
    let errors: ValidationErrors | null = null;
    if (typeof control.value === 'string' && Array.from(control.value).length > limit) {
      errors = {characterLimit: {limit}};
    }

    return errors;
  };

  return validator;
}

/** Creates complete editable preferences without putting calculated URL metadata into controls. */
export function createSiteWidePresentationForm(): FormGroup<PresentationControls>
{
  const defaults = siteWideUnlockDefaults;
  const form = new FormGroup<PresentationControls>({
    primaryColor: new FormControl(defaults.primaryColor, {nonNullable: true, validators: Validators.pattern(colorPattern)}),
    secondaryColor: new FormControl(defaults.secondaryColor, {nonNullable: true, validators: Validators.pattern(colorPattern)}),
    ctaLabel: new FormControl(defaults.ctaLabel, {nonNullable: true}),
    formTitle: new FormControl(defaults.formTitle, {nonNullable: true}),
    formSubtitle: new FormControl(defaults.formSubtitle, {nonNullable: true}),
    bannerHeadline: new FormControl(defaults.bannerHeadline, {nonNullable: true}),
    bannerBody: new FormControl(defaults.bannerBody, {nonNullable: true}),
    popupHeadline: new FormControl(defaults.popupHeadline, {nonNullable: true}),
    popupBody: new FormControl(defaults.popupBody, {nonNullable: true}),
    navigationLabel: new FormControl(defaults.navigationLabel, {nonNullable: true}),
    destinationUrlOverride: new FormControl('', {nonNullable: true, validators: characterLimit(siteWideUrlLimit)})
  });
  form.controls.primaryColor.addValidators(Validators.required);
  form.controls.secondaryColor.addValidators(Validators.required);
  for (const field of siteWideTextFields) {
    form.controls[field.name].setValidators(characterLimit(field.limit));
  }

  return form;
}

/** Preserves explicit blanks while supplying defaults for older API responses. */
export function siteWidePresentationValues(configuration: IpopConfiguration): PresentationFormValue
{
  const preferences = {...siteWideUnlockDefaults, ...configuration.siteWideUnlockPresentation};
  const values = {...preferences, destinationUrlOverride: preferences.destinationUrlOverride ?? ''};

  return values;
}

/** Disabled widget controls retain preferences; unrelated saves omit all targeting fields. */
export function siteWideUnlockPayload(form: SiteWideUnlockForm | null | undefined): Partial<IpopConfigurationUpdate>
{
  let payload: Partial<IpopConfigurationUpdate> = {};
  if (form?.dirty && form.controls.siteWideUnlockEnabled.enabled && form.valid) {
    payload = widgetAndPresentationPayload(form);
    const pageTypes: Partial<SiteWideUnlockPageTypes> = {};
    for (const {key} of siteWidePageTypeOptions) {
      const control = form.controls.pageTypes.controls[key];
      if (control.dirty) {
        pageTypes[key] = control.value;
      }
    }
    if (Object.keys(pageTypes).length > 0) {
      payload.siteWideUnlockPageTypes = pageTypes;
    }
    const paths = form.controls.customPaths.controls.map(customPathChange).filter(change => change !== null);
    if (paths.length > 0) {
      payload.siteWideUnlockCustomPaths = paths;
    }
  }

  return payload;
}

/** Presentation-only edits save independently without rewriting widget enablement or placements. */
function widgetAndPresentationPayload(form: SiteWideUnlockForm): Partial<IpopConfigurationUpdate>
{
  const payload: Partial<IpopConfigurationUpdate> = {};
  const values = form.getRawValue();
  const widgetControls = ['bannerEnabled', 'unlockPopupEnabled', 'navigationPopupEnabled', 'unlockDesktop', 'navigationDesktop'] as const;
  const masterChanged = form.controls.siteWideUnlockEnabled.dirty;
  if (masterChanged) {
    payload.siteWideUnlockEnabled = values.siteWideUnlockEnabled;
  }
  if (values.siteWideUnlockEnabled && (masterChanged || widgetControls.some(name => form.controls[name].dirty))) {
    payload.widgetSettings = [
      {widgetType: 'site_wide_unlock_banner', enabled: values.bannerEnabled, positionLayoutId: null},
      {widgetType: 'site_wide_unlock_popup', enabled: values.unlockPopupEnabled, positionLayoutId: values.unlockDesktop},
      {widgetType: 'site_wide_unlock_navigation', enabled: values.navigationPopupEnabled, positionLayoutId: values.navigationDesktop}
    ];
  }
  if (values.siteWideUnlockEnabled && form.controls.presentation.dirty) {
    payload.siteWideUnlockPresentation = {
      ...values.presentation,
      destinationUrlOverride: normalizeDestinationOverride(values.presentation.destinationUrlOverride)
    };
  }

  return payload;
}

/** Blank overrides restore live inheritance instead of copying the account default. */
function normalizeDestinationOverride(value: string | null): string | null
{
  let override = value?.trim() ?? null;
  if (override === '') {
    override = null;
  }

  return override;
}

/** Rejects browser URL ambiguities and unsupported schemes before hostname comparison. */
function parseHttpUrl(value: string): URL | null
{
  let parsed: URL | null = null;
  if (httpUrlPattern.test(value) && !unsafeUrlCharacters.test(value)) {
    try {
      const candidate = new URL(value);
      if (candidate.hostname !== '' && candidate.username === '' && candidate.password === '') {
        parsed = candidate;
      }
    } catch (error: unknown) {
      if (!(error instanceof TypeError)) {
        throw error;
      }
      parsed = null;
    }
  }

  return parsed;
}

/** Accepts configured host-only websites using the same HTTPS default as the backend. */
function accountWebsite(value: string): URL | null
{
  let candidate = value.trim();
  if (candidate !== '' && !candidate.includes('://') && !candidate.startsWith('//')) {
    candidate = 'https://' + candidate;
  }
  const website = parseHttpUrl(candidate);

  return website;
}

/** Resolves only root-relative or absolute same-host destinations against the account website. */
function destinationValidationMessage(candidate: string, websiteUrl: string): string | null
{
  const website = accountWebsite(websiteUrl);
  let message: string | null = destinationError;
  let value = candidate.trim();
  if (website === null) {
    message = 'Set a valid Account > Website website URL before configuring the navigation destination.';
  }
  if (website !== null) {
    if (value.startsWith('/') && !value.startsWith('//')) {
      value = website.origin + value;
    }
    const destination = parseHttpUrl(value);
    if (destination !== null && destination.hostname.toLowerCase() === website.hostname.toLowerCase()) {
      message = null;
    }
    if (value === '') {
      message = 'Set Account > Website new leaderboard URL or an override, or disable navigation.';
    }
  }

  return message;
}

/** Checks active navigation even before the tab mounts, and invalid overrides that would be submitted. */
export function siteWideUnlockDestinationError(
  configuration: IpopConfiguration | null,
  form: SiteWideUnlockForm | null,
  websiteUrl: string
): string | null
{
  let message: string | null = null;
  if (configuration?.canConfigureSiteWideUnlock) {
    const values = form?.getRawValue();
    const masterEnabled = values?.siteWideUnlockEnabled ?? configuration.siteWideUnlockEnabled ?? false;
    const navigation = configuration.widgetSettings?.find(row => row.widgetType === 'site_wide_unlock_navigation');
    const navigationEnabled = values?.navigationPopupEnabled ?? navigation?.enabled ?? false;
    const savedOverride = configuration.siteWideUnlockPresentation?.destinationUrlOverride ?? null;
    const override = normalizeDestinationOverride(values?.presentation.destinationUrlOverride ?? savedOverride);
    const hasSubmittedOverride = form?.controls.presentation.dirty === true && override !== null;
    if (masterEnabled && (navigationEnabled || hasSubmittedOverride)) {
      const candidate = override ?? configuration.siteWideUnlockPresentation?.defaultUrl ?? '';
      message = destinationValidationMessage(candidate, websiteUrl);
    }
  }

  return message;
}

/** Uses only the current account website when validating a parent settings shell's destination. */
export function siteWideUnlockAccountDestinationError(
  account: AccountInterface | null | undefined,
  accountId: number,
  configuration: IpopConfiguration | null,
  form: SiteWideUnlockForm | null
): string | null
{
  let websiteUrl = '';
  if (account?.id === accountId) {
    websiteUrl = account.websiteUrl;
  }
  const message = siteWideUnlockDestinationError(configuration, form, websiteUrl);

  return message;
}

/** Keeps destination-specific API feedback actionable while preserving generic errors for other failures. */
export function siteWideUnlockServerError(error: unknown): string | null
{
  let message: string | null = null;
  let detail: unknown = error;
  if (error instanceof HttpErrorResponse) {
    detail = error.error;
  }
  if (detail !== null && typeof detail === 'object') {
    const problem = detail as Record<string, unknown>;
    detail = problem.detail ?? problem['hydra:description'] ?? problem.message;
  }
  if (typeof detail === 'string'
    && (detail.startsWith('Sitewide Unlock navigation destination:') || detail.startsWith('destinationUrlOverride'))) {
    message = detail;
  }

  return message;
}

/** Prevents partial GETs from becoming a fallback all-off category submission. */
export function validPageTypes(value: unknown): value is SiteWideUnlockPageTypes
{
  const valid = value !== null && typeof value === 'object' && !Array.isArray(value)
    && siteWidePageTypeOptions.every(({key}) => typeof value[key] === 'boolean');

  return valid;
}

/** Locks drafts after a successful PUT until authoritative IDs and normalized paths are reloaded. */
export function awaitSiteWideUnlockReadback(form: SiteWideUnlockForm | null): void
{
  if (form?.dirty) {
    form.controls.requiresReload.setValue(true, {emitEvent: false});
    form.updateValueAndValidity({emitEvent: false});
  }
}

/** Rejects partial targeting readback before the shells can discard the submitted draft. */
export function confirmSiteWideUnlockReadback(form: SiteWideUnlockForm | null, settings: IpopConfiguration): void
{
  if (form?.dirty && settings.canConfigureSiteWideUnlock === true
    && (!validPageTypes(settings.siteWideUnlockPageTypes) || !validCustomPathOptions(settings.siteWideUnlockCustomPaths))) {
    throw new Error('Saved Sitewide targeting could not be fully reloaded.');
  }
}

/** Retains rejected drafts and exposes batch validation errors beside every changed path. */
export function reportSiteWideUnlockSaveError(form: SiteWideUnlockForm | null, error: unknown): void
{
  if (form) {
    const rejectedRequest = error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500;
    if (!rejectedRequest && form.dirty) {
      awaitSiteWideUnlockReadback(form);
    }
    let message = 'Unable to save settings. Your edits are retained.';
    if (rejectedRequest) {
      message = validationMessage(error.error);
    }
    if (form.controls.requiresReload.value) {
      message = 'Unable to confirm saved settings. Reload this page before making further changes; your current edits are shown below.';
    }
    form.controls.saveError.setValue(message, {emitEvent: false});
    for (const row of form.controls.customPaths.controls) {
      if (customPathChange(row) !== null) {
        row.controls.saveError.setValue(message, {emitEvent: false});
      }
    }
    form.updateValueAndValidity({emitEvent: false});
  }
}

/** Reads API Problem/JSON-LD validation details without rendering server HTML. */
function validationMessage(error: unknown): string
{
  let message = 'The server rejected these settings. Check your changes and try again.';
  if (error !== null && typeof error === 'object') {
    const body = error as Record<string, unknown>;
    const detail = body.detail ?? body['hydra:description'] ?? body.message;
    if (typeof detail === 'string' && detail.trim().length > 0) {
      message = detail;
    }
  }
  if (typeof error === 'string' && error.trim().length > 0 && !error.trim().startsWith('<')) {
    message = error;
  }

  return message;
}
